import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { Pencil, ArrowLeftRight, ArrowLeft, Download, Mail, AlertTriangle, Plus, Trash2, BadgeCheck, RotateCw, Ban, StickyNote } from "lucide-react";
import { useSetTopbarCrumbs } from "@marekl/adminer/shell";
import {
  Page, Card, Table, Button, Modal, Input, Textarea, Select, FormSectionLabel,
  FormStack, Alert, Badge, Spinner, ResponsiveGrid, DescriptionList, Text,
  Pagination, FilterPanel, FilterField, DatePicker,
} from "@marekl/adminer/components";
import {
  useCertificateList,
  useLookupCertificate,
  useCreateCertificate,
  useUpdateCertificate,
  useDeleteCertificate,
  useUnredeemCertificateItem,
  useResendCertificateEmail,
  useMarkCertificatePaid,
  useExtendCertificateValidity,
  useCancelCertificate,
  type CertificateResponse,
  type CertificateItemResponse,
  type CertificatePaymentState,
  type CreateCertificatePayload,
  type UpdateCertificatePayload,
  type ManualCertItemPayload,
  type RedeemItemResponse,
} from "../../lib/queries/certificates";
import { sentEmailNotice } from "../../lib/queries/emails";
import { useVoucherTypes } from "../../lib/queries/voucherTypes";
import { useMerchKinds } from "../../lib/queries/merchKinds";
import { ApiError } from "../../lib/api";
import { formatDate, formatDateTime } from "@marekl/adminer/format";
import { notifyInfo, notifySuccess } from "@marekl/adminer/api";
import { usePaginatedQuery, useDebouncedSearchFilter } from "@marekl/adminer/hooks";
import { useAuth } from "../../auth/useAuth";
import { ScanInput } from "../../components/ScanInput";
import { normalizeScan } from "../../lib/scan";
import { useScanKeyboardCapture } from "../../lib/useScanKeyboardCapture";
import { ItemsEditor, type ItemDraft } from "./ItemsEditor";
import { RedeemItemRow } from "./RedeemItemRow";
import { CertificateEmailsCard } from "./CertificateEmailsCard";
import { markPaidFailed, resendFailed } from "./markPaid";
import styles from "./CertificatesPage.module.css";

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; cert: CertificateResponse }
  | { kind: "confirmDelete"; cert: CertificateResponse }
  | { kind: "confirmMarkPaid"; cert: CertificateResponse }
  | { kind: "extendValidity"; cert: CertificateResponse }
  | { kind: "confirmCancel"; cert: CertificateResponse };

interface CertFormState {
  eshop_order_number: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  payment_state: CertificatePaymentState;
  note: string;
}

const EMPTY_CERT: CertFormState = {
  eshop_order_number: "",
  customer_name: "",
  customer_phone: "",
  customer_email: "",
  payment_state: "paid",
  note: "",
};

const EMPTY_ITEM: ItemDraft = {
  kind: "voucher",
  product_name: "",
  product_code: "",
  voucher_type_id: "",
  merch_kind_id: "",
};

function certToForm(cert: CertificateResponse): CertFormState {
  return {
    eshop_order_number: cert.eshop_order_number,
    customer_name: cert.customer_name,
    customer_phone: cert.customer_phone ?? "",
    customer_email: cert.customer_email,
    payment_state: cert.payment_state,
    note: cert.note ?? "",
  };
}

function paymentStateBadge(state: string) {
  switch (state) {
    case "paid":      return <Badge variant="ok">Zaplaceno</Badge>;
    case "pending":   return <Badge variant="warn">Čeká na platbu</Badge>;
    case "cancelled": return <Badge variant="err">Stornováno</Badge>;
    case "refunded":  return <Badge variant="err">Vráceno</Badge>;
    default:          return <Badge>{state}</Badge>;
  }
}

// --------------------------------------------------------------------------
// Main page
// --------------------------------------------------------------------------

export function CertificatesPage() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  // PDF download + e-mail resend are manager-level (managerMW); the list is
  // manager-only too. Create / edit / delete are superadmin-only (superMW).
  const isManager = user?.role === "manager" || user?.role === "superadmin";
  const isSuperadmin = user?.role === "superadmin";

  // ----- List query (always mounted so the list is fresh on return) --------
  const q = params.get("q") ?? "";
  const paymentState = params.get("payment_state") ?? "";

  // The list endpoint is manager-only; reception sees just the scan box, so
  // the list query stays disabled for them.
  const result = usePaginatedQuery(useCertificateList, {
    q: q || undefined,
    payment_state: paymentState || undefined,
    enabled: isManager,
  }, {
    searchParams: params,
    setSearchParams: setParams,
    defaultLimit: 50,
  });

  const search = useDebouncedSearchFilter({
    value: q,
    onCommit: (val) => result.filterReset((p) => {
      if (val) p.set("q", val); else p.delete("q");
    }),
  });
  const rows = result.data?.items ?? [];

  // ----- Shared data + mutations -------------------------------------------
  const { data: vtData = [] } = useVoucherTypes();
  const { data: mkData = [] } = useMerchKinds();
  const lookup = useLookupCertificate();
  const createCert = useCreateCertificate();
  const updateCert = useUpdateCertificate();
  const deleteCert = useDeleteCertificate();
  const resendEmail = useResendCertificateEmail();
  const markPaid = useMarkCertificatePaid();
  const cancelCert = useCancelCertificate();

  // ----- Detail (redeem) state ---------------------------------------------
  const [cert, setCert] = useState<CertificateResponse | null>(null);
  const [scanValue, setScanValue] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [highlightedItemId, setHighlightedItemId] = useState<number | null>(null);
  const [lastRedeem, setLastRedeem] = useState<RedeemItemResponse | null>(null);
  const [confirmMarkPaidDetail, setConfirmMarkPaidDetail] = useState(false);
  const [markPaidError, setMarkPaidError] = useState<string | null>(null);
  const scanRef = useRef<HTMLInputElement>(null);

  // ----- Resend-email box (editable recipient) -----------------------------
  const [resendTarget, setResendTarget] = useState<{ certId: number; defaultEmail: string } | null>(null);
  const [resendEmailValue, setResendEmailValue] = useState("");
  const [resendError, setResendError] = useState<string | null>(null);

  // Route bare digit keypresses to whichever scan field is currently mounted
  // (list-mode top scanner or detail-mode scan card). Yields to open modals.
  useScanKeyboardCapture(scanRef, (v) => {
    setScanValue(v);
    setScanError(null);
  });

  // ----- Create / edit / delete / mark-paid modal state --------------------
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [form, setForm] = useState<CertFormState>(EMPTY_CERT);
  const [items, setItems] = useState<ItemDraft[]>([{ ...EMPTY_ITEM }]);
  const [formError, setFormError] = useState<string | null>(null);
  const [unredeemingItemId, setUnredeemingItemId] = useState<number | null>(null);
  const [unredeemConfirmItem, setUnredeemConfirmItem] = useState<CertificateItemResponse | null>(null);

  const editCertId = modal.kind === "edit" ? modal.cert.id : 0;
  const unredeemItem = useUnredeemCertificateItem(editCertId);

  // ----- Extend-validity modal state ----------------------------------------
  const [extendDate, setExtendDate] = useState<dayjs.Dayjs | null>(null);
  const [extendReason, setExtendReason] = useState("");
  const [extendError, setExtendError] = useState<string | null>(null);
  const extendCertId = modal.kind === "extendValidity" ? modal.cert.id : 0;
  const extendValidity = useExtendCertificateValidity(extendCertId);

  // ----- Cancel (storno) modal state -----------------------------------------
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState<string | null>(null);

  // ----- The ?code=… query param is the source of truth for detail mode -----
  // Driving the open/closed certificate from the URL (rather than local state
  // alone) means every open/close is a real history entry, so the browser
  // back/forward buttons move between the list and a certificate detail.
  const codeParam = params.get("code");
  const lastLookedUpCode = useRef<string | null>(null);

  function setCodeParam(code: string | null) {
    const next = new URLSearchParams(params);
    if (code) next.set("code", code); else next.delete("code");
    setParams(next); // push a history entry (no replace)
  }

  useEffect(() => {
    if (!codeParam) {
      // List mode — also reached by pressing browser-back from a detail.
      lastLookedUpCode.current = null;
      setCert(null);
      setHighlightedItemId(null);
      setLastRedeem(null);
      setScanValue("");
      setScanError(null);
      setConfirmMarkPaidDetail(false);
      setMarkPaidError(null);
      scanRef.current?.focus();
      return;
    }
    // A new code in the URL we haven't loaded yet (deep-link, scan, or
    // forward-nav) → resolve it. openCert() pre-seeds lastLookedUpCode so a
    // row click reuses the already-loaded row without a refetch.
    if (codeParam !== lastLookedUpCode.current) {
      lastLookedUpCode.current = codeParam;
      void doLookup(codeParam);
    }
  // doLookup is stable within the component scope; only re-run when the URL
  // code param changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeParam]);

  // ----- Lookup / scan ------------------------------------------------------
  async function doLookup(code: string) {
    const trimmed = normalizeScan(code);
    if (!trimmed) return;
    setScanError(null);
    setHighlightedItemId(null);
    setLastRedeem(null);
    try {
      const found = await lookup.mutateAsync({ code: trimmed });
      setCert(found);
      const matchItem = found.items.find((i) => i.claim_barcode === trimmed);
      if (matchItem) setHighlightedItemId(matchItem.id);
      setScanValue("");
      setTimeout(() => scanRef.current?.focus(), 0);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setScanError("Certifikát nenalezen. Zkontrolujte kód a zkuste znovu.");
      } else {
        setScanError("Chyba při vyhledávání certifikátu.");
      }
      setTimeout(() => scanRef.current?.focus(), 0);
    }
  }

  function handleScan(raw: string) {
    const code = normalizeScan(raw);
    if (!code) return;
    // If a cert is loaded and the scanned code matches a claim barcode, just
    // highlight that row; otherwise route the lookup through the URL so it
    // lands in history (the back button returns to the previous view).
    if (cert) {
      const matchItem = cert.items.find((i) => i.claim_barcode === code);
      if (matchItem) {
        setHighlightedItemId(matchItem.id);
        setScanError(null);
        setScanValue("");
        return;
      }
    }
    setCodeParam(code);
  }

  function openCert(c: CertificateResponse) {
    // Pre-seed so the effect reuses this already-loaded row (no refetch) and
    // push ?code= so the open detail is a history entry.
    lastLookedUpCode.current = c.redemption_code;
    setCert(c);
    setHighlightedItemId(null);
    setLastRedeem(null);
    setScanError(null);
    setScanValue("");
    setCodeParam(c.redemption_code);
    setTimeout(() => scanRef.current?.focus(), 0);
  }

  function backToList() {
    lookup.reset();
    // Clearing ?code= drives the effect, which resets the detail state and
    // returns to the list (a new history entry, mirroring browser-back).
    setCodeParam(null);
    setTimeout(() => scanRef.current?.focus(), 0);
  }

  function handleRedeemed(redeemResult: RedeemItemResponse) {
    setLastRedeem(redeemResult);
    setHighlightedItemId(null);
    setCert((prev) => {
      if (!prev) return prev;
      const updatedItems = prev.items.map((i) =>
        i.id === redeemResult.item.id ? redeemResult.item : i,
      );
      const allDone = updatedItems.every((i) => i.state !== "pending");
      return {
        ...prev,
        items: updatedItems,
        redeemed_at: allDone ? redeemResult.item.redeemed_at : prev.redeemed_at,
      };
    });
  }

  function handleUnredeemedDetail(updated: CertificateItemResponse) {
    setHighlightedItemId(null);
    setLastRedeem(null);
    setCert((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((i) => (i.id === updated.id ? updated : i)),
        redeemed_at: null,
      };
    });
  }

  // ----- Create / edit modal -----------------------------------------------
  function openCreate() {
    setForm(EMPTY_CERT);
    setItems([{ ...EMPTY_ITEM }]);
    setFormError(null);
    setModal({ kind: "create" });
  }

  function openEdit(c: CertificateResponse) {
    setForm(certToForm(c));
    setFormError(null);
    setModal({ kind: "edit", cert: c });
  }

  function openConfirmDelete(c: CertificateResponse) {
    setModal({ kind: "confirmDelete", cert: c });
  }

  function closeModal() {
    setModal({ kind: "closed" });
  }

  function setField<K extends keyof CertFormState>(k: K, v: CertFormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
    setFormError(null);
  }

  function validateForm(): string | null {
    if (!form.customer_name.trim()) return "Jméno zákazníka je povinné.";
    if (!form.customer_email.trim()) return "E-mail zákazníka je povinný.";
    if (modal.kind === "create") {
      if (items.length === 0) return "Certifikát musí mít alespoň jednu položku.";
      for (const it of items) {
        if (it.kind === "voucher" && !it.voucher_type_id) return "Vyberte typ voucheru pro každou voucher položku.";
        if (it.kind === "merch" && !it.merch_kind_id) return "Vyberte produkt pro každou merch položku.";
      }
    }
    return null;
  }

  async function handleSave() {
    const err = validateForm();
    if (err) { setFormError(err); return; }

    const phone = form.customer_phone.trim() || null;

    try {
      if (modal.kind === "create") {
        const payload: CreateCertificatePayload = {
          eshop_order_number: form.eshop_order_number.trim() || undefined,
          customer_name: form.customer_name.trim(),
          customer_phone: phone,
          customer_email: form.customer_email.trim(),
          payment_state: form.payment_state,
          note: form.note.trim() || null,
          items: items.map((it): ManualCertItemPayload => {
            if (it.kind === "voucher") {
              return {
                kind: "voucher",
                product_code: it.product_code.trim() || undefined,
                product_name: vtData.find((v) => String(v.id) === it.voucher_type_id)?.name ?? it.product_name,
                voucher_type_id: it.voucher_type_id ? Number(it.voucher_type_id) : undefined,
              };
            }
            const mk = mkData.find((m) => String(m.id) === it.merch_kind_id);
            return {
              kind: "merch",
              product_code: it.product_code.trim() || undefined,
              product_name: mk?.name ?? it.product_name.trim(),
              merch_kind_id: it.merch_kind_id ? Number(it.merch_kind_id) : undefined,
            };
          }),
        };
        const created = await createCert.mutateAsync(payload);
        notifySuccess(`Certifikát ${created.redemption_code} byl vytvořen.`);
        closeModal();
      } else if (modal.kind === "edit") {
        const payload: UpdateCertificatePayload = {
          eshop_order_number: form.eshop_order_number.trim() || undefined,
          customer_name: form.customer_name.trim(),
          customer_phone: phone,
          customer_email: form.customer_email.trim(),
          payment_state: form.payment_state,
          note: form.note.trim() || null,
        };
        const updated = await updateCert.mutateAsync({ id: modal.cert.id, payload });
        notifySuccess("Certifikát byl aktualizován.");
        // Keep the open detail view in sync (edit never touches the items).
        if (cert && cert.id === modal.cert.id) {
          setCert((prev) => (prev ? { ...updated, items: prev.items } : updated));
        }
        closeModal();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | null;
        setFormError(body?.message ?? "Uložení se nezdařilo.");
      } else {
        setFormError("Uložení se nezdařilo. Zkuste to znovu.");
      }
    }
  }

  const isSaving = createCert.isPending || updateCert.isPending;
  const isCreate = modal.kind === "create";

  async function handleDelete() {
    if (modal.kind !== "confirmDelete") return;
    const deletedId = modal.cert.id;
    try {
      await deleteCert.mutateAsync({ id: deletedId });
      notifySuccess("Certifikát byl smazán.");
      closeModal();
      if (cert && cert.id === deletedId) backToList();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | null;
        setFormError(body?.message ?? "Smazání se nezdařilo.");
      } else {
        setFormError("Smazání se nezdařilo. Zkuste to znovu.");
      }
    }
  }

  function handleUnredeemEdit(item: CertificateItemResponse) {
    if (modal.kind !== "edit") return;
    setUnredeemConfirmItem(item);
  }

  async function confirmUnredeemEdit() {
    if (modal.kind !== "edit" || !unredeemConfirmItem) return;
    const item = unredeemConfirmItem;
    setUnredeemingItemId(item.id);
    try {
      await unredeemItem.mutateAsync({ itemId: item.id });
      notifySuccess(`Výdej položky „${item.product_name}" byl zrušen.`);
      setUnredeemConfirmItem(null);
      setModal((prev) => {
        if (prev.kind !== "edit") return prev;
        return {
          ...prev,
          cert: {
            ...prev.cert,
            items: prev.cert.items.map((i) =>
              i.id === item.id
                ? { ...i, state: "pending" as const, redeemed_at: null, redeemed_voucher_id: null, redeemed_by_user_id: null }
                : i
            ),
          },
        };
      });
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | null;
        setFormError(body?.message ?? "Zrušení výdeje se nezdařilo.");
      } else {
        setFormError("Zrušení výdeje se nezdařilo. Zkuste to znovu.");
      }
    } finally {
      setUnredeemingItemId(null);
    }
  }

  function handleDownloadPDF(certId: number) {
    window.open(`/api/certificates/${certId}/pdf`, "_blank");
  }

  // Open the resend-email box prefilled with the customer's address. The
  // recipient can be edited before sending.
  function openResend(certId: number, defaultEmail: string) {
    setResendTarget({ certId, defaultEmail });
    setResendEmailValue(defaultEmail);
    setResendError(null);
  }

  async function confirmResend() {
    if (!resendTarget) return;
    const email = resendEmailValue.trim();
    if (!email) { setResendError("Zadejte e-mailovou adresu."); return; }
    try {
      const sent = await resendEmail.mutateAsync({ id: resendTarget.certId, email });
      notifySuccess(sentEmailNotice(sent));
      setResendTarget(null);
    } catch (err) {
      setResendError(resendFailed(err));
    }
  }

  // Mark-paid from a table row (list mode) — confirmed via modal. The e-mail
  // goes out first: most errors mean nothing was recorded, and an e-mail
  // letterman will never deliver comes back as a warning on a saved payment.
  async function handleMarkPaidRow(c: CertificateResponse) {
    try {
      const updated = await markPaid.mutateAsync({ id: c.id });
      notifyMarkPaid(c.redemption_code, updated.email_warning);
      closeModal();
    } catch (err) {
      setMarkPaidError(markPaidFailed(err));
    }
  }

  // Mark-paid from the open detail view — updates the loaded cert in place.
  async function handleMarkPaidDetail() {
    if (!cert) return;
    setMarkPaidError(null);
    try {
      const updated = await markPaid.mutateAsync({ id: cert.id });
      setCert((prev) => (prev ? { ...updated, items: prev.items } : updated));
      setConfirmMarkPaidDetail(false);
      notifyMarkPaid(cert.redemption_code, updated.email_warning);
    } catch (err) {
      setMarkPaidError(markPaidFailed(err));
    }
  }

  // The payment is recorded; email_warning is set when letterman will never
  // deliver the certificate's e-mail, which reception has to act on.
  function notifyMarkPaid(code: string, warning?: string) {
    const paid = `Certifikát ${code} byl označen jako uhrazený.`;
    if (warning) {
      notifyInfo(`${paid} ${warning}`);
      return;
    }
    notifySuccess(paid);
  }

  // ----- Cancel (storno) handlers --------------------------------------------
  function openConfirmCancel(c: CertificateResponse) {
    setCancelReason("");
    setCancelError(null);
    setModal({ kind: "confirmCancel", cert: c });
  }

  async function handleConfirmCancel() {
    if (modal.kind !== "confirmCancel") return;
    const reason = cancelReason.trim();
    if (!reason) {
      setCancelError("Důvod stornování je povinný.");
      return;
    }
    setCancelError(null);
    try {
      const updated = await cancelCert.mutateAsync({ id: modal.cert.id, reason });
      notifySuccess(`Certifikát ${modal.cert.redemption_code} byl stornován.`);
      setCert((prev) => (prev && prev.id === updated.id ? updated : prev));
      closeModal();
    } catch (err) {
      setCancelError(
        err instanceof ApiError
          ? (err.body as { message?: string } | null)?.message ?? "Storno se nezdařilo."
          : "Storno se nezdařilo. Zkuste to znovu.",
      );
    }
  }

  // ----- Extend-validity handlers -------------------------------------------
  function openExtendValidity(c: CertificateResponse) {
    const current = c.valid_until ? c.valid_until : dayjs();
    const minNext = current.add(1, "day").startOf("day");
    const suggested = dayjs().add(6, "month").startOf("day");
    setExtendDate(suggested.isAfter(minNext) ? suggested : minNext);
    setExtendReason("");
    setExtendError(null);
    setModal({ kind: "extendValidity", cert: c });
  }

  async function handleConfirmExtendValidity() {
    if (modal.kind !== "extendValidity") return;
    if (!extendDate || !extendDate.isValid()) {
      setExtendError("Vyberte nové datum platnosti.");
      return;
    }
    if (extendReason.trim().length < 3) {
      setExtendError("Uveďte prosím důvod (min. 3 znaky).");
      return;
    }
    try {
      const updated = await extendValidity.mutateAsync({
        valid_until: extendDate.endOf("day").toISOString(),
        reason: extendReason.trim(),
      });
      notifySuccess(`Platnost certifikátu ${modal.cert.redemption_code} prodloužena.`);
      if (cert && cert.id === modal.cert.id) {
        setCert((prev) => (prev ? { ...prev, valid_until: updated.valid_until } : prev));
      }
      closeModal();
    } catch (err) {
      setExtendError(
        err instanceof ApiError
          ? (err.body as { message?: string } | null)?.message ?? "Prodloužení se nezdařilo."
          : "Prodloužení se nezdařilo. Zkuste to znovu.",
      );
    }
  }

  const isFullyRedeemed = cert !== null && cert.items.every((i) => i.state !== "pending");
  const isCertExpired = cert !== null && !!cert.valid_until && cert.valid_until.isBefore(dayjs());
  const isCertExpiringSoon = cert !== null && !!cert.valid_until && !isCertExpired && cert.valid_until.isBefore(dayjs().add(30, "day"));

  useSetTopbarCrumbs(
    cert
      ? [{ label: "Certifikáty" }, { label: cert.redemption_code }]
      : [{ label: "Certifikáty" }],
  );

  return (
    <>
      <Page.Content stack gap="lg">
        {cert ? (
          // ===================================================================
          // Detail / redemption mode
          // ===================================================================
          <>
            <Page.Header
              title={
                <>
                  Certifikát {cert.redemption_code}
                  {paymentStateBadge(cert.payment_state)}
                </>
              }
              subtitle={
                <>
                  Objednávka {cert.eshop_order_number} · {cert.customer_name}
                  {cert.created_at && ` · ${formatDate(cert.created_at)}`}
                </>
              }
              subtitleVariant="default"
              actions={
                <>
                  <Button variant="default" onClick={backToList}>
                    <ArrowLeft size={16} aria-hidden="true" />
                    Zpět na seznam
                  </Button>
                  {isManager && cert.payment_state === "paid" && (
                    <>
                      <Button
                        variant="default"
                        onClick={() => handleDownloadPDF(cert.id)}
                        title="Stáhnout PDF certifikátu"
                      >
                        <Download size={16} aria-hidden="true" />
                        PDF
                      </Button>
                      <Button
                        variant="default"
                        onClick={() => openResend(cert.id, cert.customer_email)}
                        title="Znovu odeslat e-mail zákazníkovi"
                      >
                        <Mail size={16} aria-hidden="true" />
                        Přeposlat e-mail
                      </Button>
                    </>
                  )}
                  {isManager && (
                    <Button variant="default" onClick={() => openExtendValidity(cert)}>
                      <RotateCw size={16} aria-hidden="true" />
                      Prodloužit platnost
                    </Button>
                  )}
                  {/* The superadmin actions are intentionally split around the
                      manager-level storno button so the danger actions
                      (Stornovat, Smazat) sit next to each other. */}
                  {isSuperadmin && (
                    <Button variant="default" onClick={() => openEdit(cert)}>
                      <Pencil size={16} aria-hidden="true" />
                      Upravit
                    </Button>
                  )}
                  {isManager && cert.payment_state === "pending" && (
                    <Button variant="danger" onClick={() => openConfirmCancel(cert)}>
                      <Ban size={16} aria-hidden="true" />
                      Stornovat
                    </Button>
                  )}
                  {isSuperadmin && (
                    <Button variant="danger" onClick={() => openConfirmDelete(cert)}>
                      <Trash2 size={16} aria-hidden="true" />
                      Smazat
                    </Button>
                  )}
                </>
              }
            />

            {cert.payment_state === "paid" && !isFullyRedeemed && (
              <Alert variant="ok">
                <strong>Platba potvrzena.</strong> Všechny položky lze směnit.
                Naskenujte kód z certifikátu pro zvýraznění položky, poté ji potvďte tlačítkem.
              </Alert>
            )}
            {cert.payment_state === "pending" && (
              <Alert variant="warn">
                <strong>Certifikát čeká na platbu.</strong> Pokud jste obdrželi
                potvrzení o bankovním převodu, můžete jej označit jako uhrazený
                a pokračovat ve směně.
                {" "}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => { setMarkPaidError(null); setConfirmMarkPaidDetail(true); }}
                  className={styles.markPaidBtn}
                >
                  <BadgeCheck size={14} aria-hidden="true" />
                  Označit jako uhrazené
                </Button>
              </Alert>
            )}
            {cert.payment_state !== "paid" && cert.payment_state !== "pending" && (
              <Alert variant="err">
                <strong>Certifikát nelze směnit</strong> — stav platby: {cert.payment_state}.
                {cert.payment_state === "cancelled" && cert.cancel_reason && (
                  <>
                    {" "}Důvod storna: {cert.cancel_reason}
                    {cert.cancelled_at && <> ({formatDate(cert.cancelled_at)})</>}.
                  </>
                )}
              </Alert>
            )}
            {isCertExpired && !isFullyRedeemed && (
              <Alert variant="err">
                <strong>Certifikát má prošlou platnost</strong>
                {cert.valid_until && ` — platnost skončila ${formatDate(cert.valid_until)}`}.
                {isManager && (
                  <>
                    {" "}
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => openExtendValidity(cert)}
                      className={styles.markPaidBtn}
                    >
                      <RotateCw size={14} aria-hidden="true" />
                      Prodloužit platnost
                    </Button>
                  </>
                )}
              </Alert>
            )}
            {isCertExpiringSoon && !isFullyRedeemed && (
              <Alert variant="warn">
                Certifikát vyprší {formatDate(cert.valid_until!)}.
                {isManager && (
                  <>
                    {" "}
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => openExtendValidity(cert)}
                    >
                      <RotateCw size={14} aria-hidden="true" />
                      Prodloužit
                    </Button>
                  </>
                )}
              </Alert>
            )}
            {isFullyRedeemed && cert.payment_state === "paid" && (
              <Alert variant="ok">
                <strong>Všechny položky byly směněny.</strong>
                {cert.redeemed_at && ` Certifikát uzavřen ${formatDateTime(cert.redeemed_at)}.`}
              </Alert>
            )}

            {lastRedeem?.issued_voucher && (
              <Alert variant="ok">
                Vydán voucher <Text as="strong" variant="mono">{lastRedeem.issued_voucher.barcode}</Text>
                {" · "}platí do {formatDate(lastRedeem.issued_voucher.valid_until)}.
                {" "}
                <Link to={`/vouchers/${lastRedeem.issued_voucher.id}`} className={styles.alertLink}>
                  Detail voucheru →
                </Link>
              </Alert>
            )}

            <ResponsiveGrid>
              <Card
                header={<Card.Header title="Skenování" subtitle="kód z certifikátu" />}
                bodyPadding="sm"
              >
                <ScanInput
                  ref={scanRef}
                  value={scanValue}
                  onChange={setScanValue}
                  placeholder="Skenujte kód z certifikátu…"
                  onScan={handleScan}
                  error={scanError ?? undefined}
                  variant="compact"
                />
                <p className={styles.scanHint}>
                  Po naskenování kódu se zvýrazní odpovídající řádek.
                </p>
              </Card>

              <Card
                header={<Card.Header title="Zákazník" />}
                bodyPadding="sm"
              >
                <DescriptionList>
                  <DescriptionList.Item label="Jméno">
                    {cert.customer_name}
                  </DescriptionList.Item>
                  {cert.customer_phone && (
                    <DescriptionList.Item label="Telefon">
                      {cert.customer_phone}
                    </DescriptionList.Item>
                  )}
                  <DescriptionList.Item label="E-mail">
                    {cert.customer_email}
                  </DescriptionList.Item>
                </DescriptionList>
              </Card>
            </ResponsiveGrid>

            <Card
              header={
                <Card.Header
                  title="Položky certifikátu"
                  subtitle={`${cert.items.length} ${cert.items.length === 1 ? "položka" : cert.items.length >= 2 && cert.items.length <= 4 ? "položky" : "položek"}`}
                  subtitlePlacement="right"
                />
              }
              bodyPadding="none"
            >
              <ul className={styles.itemsList}>
                {cert.items.map((item) => (
                  <RedeemItemRow
                    key={item.id}
                    cert={cert}
                    item={item}
                    highlighted={highlightedItemId === item.id}
                    onRedeemed={handleRedeemed}
                    onUnredeemed={handleUnredeemedDetail}
                  />
                ))}
              </ul>
            </Card>

            {isManager && (
              <CertificateEmailsCard certificateId={cert.id} showLettermanLinks={isSuperadmin} />
            )}

            {cert.items.some((i) => i.state === "pending") && (
              <Alert variant="warn">
                Zákazník nemusí vyzvednout vše najednou – položky lze směňovat postupně.
                Nedořešené položky lze znovu otevřít přes tuto stránku.
              </Alert>
            )}
          </>
        ) : (
          // ===================================================================
          // List mode
          // ===================================================================
          <>
            <Page.Header
              title="Certifikáty"
              subtitle={isManager ? "směna e-shop certifikátů na recepci a jejich správa" : "směna e-shop certifikátů na recepci"}
              subtitleVariant="tagline"
              actions={
                isSuperadmin ? (
                  <Button variant="primary" onClick={openCreate}>
                    <Plus size={16} aria-hidden="true" />
                    Vytvořit certifikát
                  </Button>
                ) : undefined
              }
            />

            <Card bodyPadding="sm">
              {lookup.isPending && (
                <div className={styles.lookupSpinner}><Spinner size="sm" /> Vyhledávání…</div>
              )}
              <ScanInput
                ref={scanRef}
                value={scanValue}
                onChange={setScanValue}
                placeholder="Skenujte nebo zadejte kód certifikátu…"
                onScan={handleScan}
                error={scanError ?? undefined}
                disabled={lookup.isPending}
                variant="compact"
              />
              <p className={styles.scanHint}>
                Naskenujte čárový kód z papírového certifikátu, nebo ručně zadejte redemption kód (CRT-YYYY-…) či číslo objednávky.{isManager ? " Certifikát můžete také vybrat ze seznamu níže." : ""}
              </p>
            </Card>

            {/* The list endpoint is manager-only — reception sees only the scan box above. */}
            {isManager && (
            <>
            <FilterPanel
              formProps={{ onSubmit: (e) => { e.preventDefault(); search.flush(); } }}
            >
              <FilterField wide>
                <Input
                  label="Hledaný výraz"
                  type="search"
                  placeholder="objednávka, kód, jméno, e-mail, telefon"
                  value={search.draft}
                  onChange={(e) => search.setDraft(e.target.value)}
                />
              </FilterField>
              <FilterField>
                <Select
                  label="Stav platby"
                  id="cert-payment-state"
                  value={paymentState}
                  onChange={(e) => result.filterReset((p) => {
                    const v = e.target.value;
                    if (v) p.set("payment_state", v); else p.delete("payment_state");
                  })}
                >
                  <option value="">Všechny</option>
                  <option value="pending">Čeká na platbu</option>
                  <option value="paid">Zaplaceno</option>
                  <option value="cancelled">Stornováno</option>
                  <option value="refunded">Vráceno</option>
                </Select>
              </FilterField>
              <FilterField action>
                <Button type="submit" variant="primary" block>Hledat</Button>
              </FilterField>
            </FilterPanel>

            <Table<CertificateResponse>
              cards
              columns={[
                {
                  key: "cert",
                  header: "Certifikát",
                  primary: true,
                  render: (r) => (
                    <div>
                      <div>
                        <button type="button" className={styles.codeLink} onClick={() => openCert(r)} title="Otevřít detail certifikátu">
                          {r.eshop_order_number || r.redemption_code}
                        </button>
                        {r.note && (
                          <span className={styles.noteIcon} role="img" title={r.note} aria-label={`Poznámka: ${r.note}`}>
                            <StickyNote size={14} aria-hidden="true" />
                          </span>
                        )}
                      </div>
                      {r.eshop_order_number && (
                        <div>
                          <button type="button" className={`${styles.codeLink} ${styles.codeSub}`} onClick={() => openCert(r)} title="Otevřít detail certifikátu">
                            {r.redemption_code}
                          </button>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: "customer",
                  header: "Zákazník",
                  render: (r) => (
                    <div>
                      <div>{r.customer_name}</div>
                      <Text as="div" variant="muted">{r.customer_email}</Text>
                    </div>
                  ),
                },
                {
                  key: "payment",
                  header: "Platba",
                  render: (r) => (
                    <div>
                      {paymentStateBadge(r.payment_state)}
                      {(r.payment_method_name || r.payment_method_id) && (
                        <Text as="div" variant="muted">{r.payment_method_name || r.payment_method_id}</Text>
                      )}
                    </div>
                  ),
                },
                {
                  key: "items",
                  header: "Položky",
                  render: (r) => {
                    const total = r.items?.length ?? 0;
                    const redeemed = r.items?.filter((i) => i.state === "redeemed").length ?? 0;
                    return <Text variant="muted">{redeemed}/{total}</Text>;
                  },
                },
                {
                  key: "validity",
                  header: "Platnost",
                  render: (r) => {
                    const expired = r.valid_until?.isBefore(dayjs()) ?? false;
                    return (
                      <div className={styles.dateCell}>
                        {r.valid_until ? (
                          <Text as="div" variant="muted" className={expired ? styles.expiredDate : undefined}>
                            {expired && <AlertTriangle size={12} style={{ marginRight: 4 }} aria-hidden="true" />}
                            {formatDate(r.valid_until)}
                          </Text>
                        ) : (
                          <Text as="div" variant="muted">—</Text>
                        )}
                        <Text as="div" variant="muted">vytv. {formatDate(r.created_at)}</Text>
                      </div>
                    );
                  },
                },
                {
                  key: "actions",
                  header: "",
                  render: (r) => (
                    <Table.Actions>
                      {isManager && r.payment_state === "pending" && (
                        <Table.Action
                          variant="primary"
                          onClick={() => { setMarkPaidError(null); setModal({ kind: "confirmMarkPaid", cert: r }); }}
                          title="Označit jako uhrazený"
                        >
                          <BadgeCheck size={14} />
                          Uhrazeno
                        </Table.Action>
                      )}
                      {isManager && r.valid_until && r.valid_until.isBefore(dayjs()) &&
                        r.items.some((i) => i.state === "pending") && (
                        <Table.Action
                          iconOnly
                          variant="default"
                          onClick={() => openExtendValidity(r)}
                          title="Prodloužit platnost certifikátu"
                          aria-label="Prodloužit platnost certifikátu"
                        >
                          <RotateCw size={14} />
                        </Table.Action>
                      )}
                      <Table.Action
                        variant="primary"
                        onClick={() => openCert(r)}
                        title="Otevřít směnu"
                      >
                        <ArrowLeftRight size={14} />
                        Směnit
                      </Table.Action>
                      {isSuperadmin && (
                        <>
                          <Table.Action iconOnly onClick={() => openEdit(r)} aria-label="Upravit">
                            <Pencil size={14} />
                          </Table.Action>
                          <Table.Action iconOnly variant="danger" onClick={() => openConfirmDelete(r)} aria-label="Smazat">
                            <Trash2 size={14} />
                          </Table.Action>
                        </>
                      )}
                    </Table.Actions>
                  ),
                },
              ]}
              rows={rows}
              rowKey={(r) => r.id}
              empty={
                result.isLoading
                  ? <span>Načítání…</span>
                  : <span>Žádné certifikáty.{isSuperadmin ? " Klikněte na „Vytvořit certifikát“." : ""}</span>
              }
            />

            <Pagination
              {...result.pagination}
              summary={
                result.data != null && result.data.total_estimate > 0
                  ? `${rows.length.toLocaleString("cs-CZ")} z ${result.data.total_estimate.toLocaleString("cs-CZ")} certifikátů`
                  : undefined
              }
            />
            </>
            )}
          </>
        )}
      </Page.Content>

      {/* ===== Create / Edit modal (manager+) ===== */}
      <Modal
        open={modal.kind === "create" || modal.kind === "edit"}
        title={isCreate ? "Vytvořit certifikát" : "Upravit certifikát"}
        onClose={closeModal}
        width={520}
        footer={
          <>
            <Button variant="default" onClick={closeModal} disabled={isSaving}>Zrušit</Button>
            <Button variant="primary" onClick={handleSave} loading={isSaving} disabled={isSaving}>Uložit</Button>
          </>
        }
      >
        <FormStack>
          {formError && <Alert variant="err">{formError}</Alert>}

          {!isCreate && modal.kind === "edit" && modal.cert.manual_intervention && (
            <Alert variant="warn">
              <AlertTriangle size={16} aria-hidden="true" />
              Tento certifikát vyžaduje ruční zásah (vrácení platby nebo jiný problém).
            </Alert>
          )}

          <Input
            label="Jméno zákazníka"
            value={form.customer_name}
            onChange={(e) => setField("customer_name", e.target.value)}
            placeholder="Jan Novák"
            required
          />
          <Input
            label="E-mail zákazníka"
            type="email"
            value={form.customer_email}
            onChange={(e) => setField("customer_email", e.target.value)}
            placeholder="zakaznik@example.com"
            required
          />
          <Input
            label="Telefon zákazníka (volitelné)"
            value={form.customer_phone}
            onChange={(e) => setField("customer_phone", e.target.value)}
            placeholder="+420 …"
          />
          <Input
            label="Číslo objednávky (volitelné)"
            value={form.eshop_order_number}
            onChange={(e) => setField("eshop_order_number", e.target.value)}
            placeholder="např. 2026-00123"
          />
          <Select
            label="Stav platby"
            value={form.payment_state}
            onChange={(e) => setField("payment_state", e.target.value as CertificatePaymentState)}
          >
            <option value="paid">Zaplaceno</option>
            <option value="pending">Čeká na platbu</option>
            <option value="cancelled">Stornováno</option>
            <option value="refunded">Vráceno</option>
          </Select>
          <Textarea
            label="Poznámka (volitelné)"
            value={form.note}
            onChange={(e) => setField("note", e.target.value)}
            placeholder="Poznámka zákazníka z objednávky…"
            rows={3}
          />

          {isCreate && (
            <>
              <FormSectionLabel>Položky certifikátu</FormSectionLabel>
              <ItemsEditor
                items={items}
                onChange={setItems}
                voucherTypes={vtData}
                merchKinds={mkData}
              />
            </>
          )}

          {!isCreate && modal.kind === "edit" && (
            <>
              <FormSectionLabel>Položky certifikátu</FormSectionLabel>
              {modal.cert.items?.length ? (
                <div className={styles.editItemsList}>
                  {modal.cert.items.map((item) => (
                    <div key={item.id} className={styles.editItemRow}>
                      <div className={styles.editItemInfo}>
                        <span className={styles.editItemName}>{item.product_name}</span>
                        {item.state === "redeemed" ? (
                          <Badge variant="ok">Směněno</Badge>
                        ) : item.state === "cancelled" ? (
                          <Badge variant="err">Stornováno</Badge>
                        ) : (
                          <Badge variant="warn">Čeká</Badge>
                        )}
                      </div>
                      {item.state === "redeemed" && item.kind !== "voucher" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnredeemEdit(item)}
                          loading={unredeemingItemId === item.id}
                          disabled={unredeemingItemId !== null}
                          title="Zrušit výdej a vrátit položku do stavu čeká"
                        >
                          Zrušit výdej
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="info">Tento certifikát nemá žádné položky.</Alert>
              )}
            </>
          )}

          {!isCreate && modal.kind === "edit" && modal.cert.payment_state === "paid" && (
            <>
              <FormSectionLabel>Akce certifikátu</FormSectionLabel>
              <div className={styles.certActions}>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleDownloadPDF(modal.cert.id)}
                  title="Stáhnout PDF certifikátu"
                >
                  <Download size={14} aria-hidden="true" />
                  Stáhnout PDF
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => modal.kind === "edit" && openResend(modal.cert.id, form.customer_email.trim() || modal.cert.customer_email)}
                  title="Znovu odeslat e-mail zákazníkovi"
                >
                  <Mail size={14} aria-hidden="true" />
                  Znovu odeslat e-mail
                </Button>
              </div>
            </>
          )}
          {!isCreate && modal.kind === "edit" && modal.cert.payment_state !== "paid" && (
            <Alert variant="info">
              PDF a doručovací e-mail budou dostupné po potvrzení platby (tlačítko „Uhrazeno“).
            </Alert>
          )}
        </FormStack>
      </Modal>

      {/* ===== Delete confirmation ===== */}
      <Modal
        open={modal.kind === "confirmDelete"}
        title="Smazat certifikát"
        onClose={closeModal}
        width={400}
        footer={
          <>
            <Button variant="default" onClick={closeModal} disabled={deleteCert.isPending}>Zrušit</Button>
            <Button variant="danger" onClick={handleDelete} loading={deleteCert.isPending} disabled={deleteCert.isPending}>Smazat</Button>
          </>
        }
      >
        <FormStack>
          {formError && <Alert variant="err">{formError}</Alert>}
          {modal.kind === "confirmDelete" && (
            <p>
              Opravdu chcete smazat certifikát <strong>{modal.cert.redemption_code}</strong> ({modal.cert.customer_name})?
              Tuto akci nelze vrátit. Certifikáty se směněnými položkami smazat nelze.
            </p>
          )}
        </FormStack>
      </Modal>

      {/* ===== Mark-paid confirmation (from a table row) ===== */}
      <Modal
        open={modal.kind === "confirmMarkPaid"}
        title="Označit jako uhrazené"
        onClose={closeModal}
        width={460}
        footer={
          <>
            <Button variant="default" onClick={closeModal} disabled={markPaid.isPending}>Zrušit</Button>
            <Button
              variant="primary"
              onClick={() => modal.kind === "confirmMarkPaid" && handleMarkPaidRow(modal.cert)}
              loading={markPaid.isPending}
              disabled={markPaid.isPending}
            >
              Označit jako uhrazené
            </Button>
          </>
        }
      >
        <FormStack>
          {markPaidError && <Alert variant="err">{markPaidError}</Alert>}
          {modal.kind === "confirmMarkPaid" && (
            <p>
              Označit certifikát <strong>{modal.cert.redemption_code}</strong> (
              {modal.cert.customer_name}) jako uhrazený? Zákazníkovi bude odeslán
              e-mail s certifikátem a položky bude možné směnit.
            </p>
          )}
        </FormStack>
      </Modal>

      {/* ===== Unredeem confirmation (from the edit modal) ===== */}
      <Modal
        open={unredeemConfirmItem !== null}
        title="Zrušit výdej položky"
        onClose={() => !unredeemItem.isPending && setUnredeemConfirmItem(null)}
        width={460}
        footer={
          <>
            <Button
              variant="default"
              onClick={() => setUnredeemConfirmItem(null)}
              disabled={unredeemItem.isPending}
            >
              Zrušit
            </Button>
            <Button
              variant="danger"
              onClick={confirmUnredeemEdit}
              loading={unredeemItem.isPending}
              disabled={unredeemItem.isPending}
            >
              Zrušit výdej
            </Button>
          </>
        }
      >
        {unredeemConfirmItem && (
          <p>
            Opravdu chcete zrušit výdej položky{" "}
            <strong>„{unredeemConfirmItem.product_name}"</strong>? Položka se
            vrátí do stavu k vyzvednutí a zákazník ji bude moci znovu vyzvednout.
          </p>
        )}
      </Modal>

      {/* ===== Mark-paid confirmation (from the open detail view) ===== */}
      <Modal
        open={confirmMarkPaidDetail}
        title="Označit jako uhrazené"
        onClose={() => !markPaid.isPending && setConfirmMarkPaidDetail(false)}
        width={460}
        footer={
          <>
            <Button
              variant="default"
              onClick={() => setConfirmMarkPaidDetail(false)}
              disabled={markPaid.isPending}
            >
              Zrušit
            </Button>
            <Button
              variant="primary"
              onClick={handleMarkPaidDetail}
              loading={markPaid.isPending}
              disabled={markPaid.isPending}
            >
              Označit jako uhrazené
            </Button>
          </>
        }
      >
        <FormStack>
          {markPaidError && <Alert variant="err">{markPaidError}</Alert>}
          {cert && (
            <p>
              Označit certifikát <strong>{cert.redemption_code}</strong> (
              {cert.customer_name}) jako uhrazený? Zákazníkovi bude odeslán e-mail
              s certifikátem a položky bude možné směnit.
            </p>
          )}
        </FormStack>
      </Modal>

      {/* ===== Extend certificate validity (manager+) ===== */}
      <Modal
        open={modal.kind === "extendValidity"}
        title="Prodloužit platnost certifikátu"
        onClose={() => !extendValidity.isPending && closeModal()}
        width={460}
        footer={
          <>
            <Button variant="default" onClick={closeModal} disabled={extendValidity.isPending}>Zrušit</Button>
            <Button
              variant="primary"
              onClick={handleConfirmExtendValidity}
              loading={extendValidity.isPending}
              disabled={extendValidity.isPending}
            >
              Prodloužit
            </Button>
          </>
        }
      >
        <FormStack>
          {extendError && <Alert variant="err">{extendError}</Alert>}
          {modal.kind === "extendValidity" && (
            <p>
              Certifikát <strong>{modal.cert.redemption_code}</strong>
              {modal.cert.valid_until
                ? <> — stávající platnost do <strong>{formatDate(modal.cert.valid_until)}</strong>.</>
                : " nemá nastavenou platnost."}
              {" "}Nový datum musí být pozdější.
            </p>
          )}
          <DatePicker
            label="Nové datum platnosti"
            value={extendDate}
            onChange={(d) => { setExtendDate(d); setExtendError(null); }}
            minDate={
              modal.kind === "extendValidity" && modal.cert.valid_until
                ? modal.cert.valid_until.add(1, "day")
                : dayjs().add(1, "day")
            }
          />
          <Input
            label="Důvod prodloužení"
            placeholder="např. zákazník nestihl využít certifikát"
            value={extendReason}
            onChange={(e) => { setExtendReason(e.target.value); setExtendError(null); }}
          />
        </FormStack>
      </Modal>

      {/* ===== Cancel (storno) an unpaid certificate (manager+) ===== */}
      <Modal
        open={modal.kind === "confirmCancel"}
        title="Stornovat certifikát"
        onClose={() => !cancelCert.isPending && closeModal()}
        width={460}
        footer={
          <>
            <Button variant="default" onClick={closeModal} disabled={cancelCert.isPending}>
              Zrušit
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmCancel}
              loading={cancelCert.isPending}
              disabled={cancelCert.isPending}
            >
              Stornovat certifikát
            </Button>
          </>
        }
      >
        <FormStack>
          {cancelError && <Alert variant="err">{cancelError}</Alert>}
          {modal.kind === "confirmCancel" && (
            <p>
              Certifikát <strong>{modal.cert.redemption_code}</strong> (
              {modal.cert.customer_name}) bude stornován a všechny jeho položky
              budou označeny jako stornované. Pokud zákazník zaplatí po stornu,
              platba se už automaticky nepropíše a bude nutné ji vyřešit ručně.
            </p>
          )}
          <Input
            label="Důvod stornování"
            placeholder="např. zákazník nezaplatil"
            value={cancelReason}
            onChange={(e) => { setCancelReason(e.target.value); setCancelError(null); }}
          />
        </FormStack>
      </Modal>

      {/* ===== Resend delivery e-mail (editable recipient) ===== */}
      <Modal
        open={resendTarget !== null}
        title="Přeposlat e-mail"
        onClose={() => !resendEmail.isPending && setResendTarget(null)}
        width={460}
        footer={
          <>
            <Button
              variant="default"
              onClick={() => setResendTarget(null)}
              disabled={resendEmail.isPending}
            >
              Zrušit
            </Button>
            <Button
              variant="primary"
              onClick={confirmResend}
              loading={resendEmail.isPending}
              disabled={resendEmail.isPending || resendEmailValue.trim() === ""}
            >
              Odeslat
            </Button>
          </>
        }
      >
        <FormStack>
          {resendError && <Alert variant="err">{resendError}</Alert>}
          <p>
            Certifikát s PDF přílohou bude znovu odeslán na zadanou adresu.
            Předvyplněn je e-mail zákazníka – v případě potřeby jej upravte.
          </p>
          <Input
            label="E-mail příjemce"
            type="email"
            value={resendEmailValue}
            onChange={(e) => { setResendEmailValue(e.target.value); setResendError(null); }}
            placeholder="zakaznik@example.com"
            required
          />
        </FormStack>
      </Modal>
    </>
  );
}
